import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Label } from '@voilajsx/uikit/label';
import { Edit, Loader2, Shield, Coins, Building2, Phone } from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header,  SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

interface AppSetting {
  key: string;
  value: string;
  description?: string;
}

const SETTING_GROUPS = [
  {
    title: 'Pricing Tiers',
    icon: Coins,
    keys: ['signup_bonus_credits'],
  },
  {
    title: 'Business Details',
    icon: Building2,
    keys: ['business_name', 'business_address', 'business_state', 'business_gstin'],
  },
  {
    title: 'Support',
    icon: Phone,
    keys: ['support_phone', 'support_whatsapp_url'],
  },
];

const SETTING_LABELS: Record<string, string> = {
  signup_bonus_credits: 'Signup Bonus Credits',
  business_name: 'Business Name',
  business_address: 'Business Address',
  business_state: 'Business State',
  business_gstin: 'Business GSTIN',
  support_phone: 'Support Phone',
  support_whatsapp_url: 'Support WhatsApp URL',
};

const formatSettingLabel = (key: string): string =>
  SETTING_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AdminSettingsPage: React.FC = () => {
  const { user, token } = useAuth();

  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editDialog, setEditDialog] = useState<{ open: boolean; key: string; value: string }>({
    open: false,
    key: '',
    value: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/settings/admin/list`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setSettings(data.data?.settings || data.settings || data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminSystem && token) fetchSettings();
  }, [token, isAdminSystem]);

  if (!user) return null;

  const openEditDialog = (key: string, currentValue: string) => {
    setEditDialog({ open: true, key, value: currentValue });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/settings/admin/${editDialog.key}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ value: editDialog.value }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}: ${res.statusText}`);
      }

      setSuccess(`Setting "${formatSettingLabel(editDialog.key)}" updated successfully`);
      setEditDialog({ open: false, key: '', value: '' });
      fetchSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save setting');
    } finally {
      setIsSaving(false);
    }
  };

  const getSettingValue = (key: string): string => {
    const setting = settings.find((s) => s.key === key);
    return setting?.value || '';
  };

  if (!isAdminSystem) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">Only system administrators can manage app settings.</p>
                  <Button asChild><a href={route('/admin')}>Back to Admin</a></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="App Settings" description="Configure application settings" />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">App Settings</h1>
            <p className="text-muted-foreground">Configure application-wide settings</p>
          </div>

          {success && (
            <Alert className="bg-green-950 border-green-800 text-green-300">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="bg-destructive/10 border-destructive text-destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading settings...</span>
            </div>
          ) : (
            SETTING_GROUPS.map((group) => (
              <Card key={group.title} className="border-zinc-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <group.icon className="h-5 w-5" />
                    {group.title}
                  </CardTitle>
                  <CardDescription>
                    {group.title === 'Pricing Tiers' && 'Credits granted automatically on user signup'}
                    {group.title === 'Business Details' && 'Business registration and tax information'}
                    {group.title === 'Support' && 'Customer support contact details'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {group.keys.map((key) => {
                      const value = getSettingValue(key);
                      const displayValue = key === 'signup_bonus_credits' && value ? `₹ ${value} credits` : value;
                      return (
                        <div key={key} className="flex items-center justify-between py-3 border-b border-zinc-700 last:border-0">
                          <div>
                            <p className="text-sm font-medium">{formatSettingLabel(key)}</p>
                            <p className="text-sm text-muted-foreground font-mono">
                              {displayValue || <span className="italic text-zinc-500">Not set</span>}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Edit"
                            onClick={() => openEditDialog(key, value)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
          </div>
        </div>
      </PageLayout.Content>


      {/* Edit Setting Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, key: '', value: '' })}>
        <DialogContent className="max-w-md bg-[#1e1e1e] border-zinc-700 text-foreground">
          <DialogHeader>
            <DialogTitle>Edit Setting</DialogTitle>
            <DialogDescription>
              Update the value for <span className="font-medium">{formatSettingLabel(editDialog.key)}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="settingKey">Key</Label>
              <Input id="settingKey" value={editDialog.key} disabled className="font-mono text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settingValue">Value</Label>
              <Input
                id="settingValue"
                value={editDialog.value}
                onChange={(e) => setEditDialog({ ...editDialog, value: e.target.value })}
                placeholder="Enter value"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, key: '', value: '' })} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-2">
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminSettingsPage;
